import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { fetchProjectBySlug } from '../../lib/projects';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';

const richTextOptions = {
    renderMark: {
        [MARKS.BOLD]: (text) => <strong className="font-semibold text-neutral-100">{text}</strong>,
    },
    renderNode: {
        [BLOCKS.HEADING_1]: (node, children) => <h3 className="mt-10 mb-4 text-3xl font-semibold text-white">{children}</h3>,
        [BLOCKS.HEADING_2]: (node, children) => <h3 className="mt-10 mb-4 text-2xl font-semibold text-white">{children}</h3>,
        [BLOCKS.HEADING_3]: (node, children) => <h4 className="mt-8 mb-3 text-xl font-semibold text-neutral-100">{children}</h4>,
        [BLOCKS.PARAGRAPH]: (node, children) => <p className="mb-5 text-base leading-8 text-neutral-400 md:text-lg">{children}</p>,
        [BLOCKS.UL_LIST]: (node, children) => <ul className="mb-6 ml-5 list-disc space-y-3 text-neutral-400 marker:text-[#A2BC6B]">{children}</ul>,
        [BLOCKS.OL_LIST]: (node, children) => <ol className="mb-6 ml-5 list-decimal space-y-3 text-neutral-400 marker:text-[#A2BC6B]">{children}</ol>,
        [BLOCKS.QUOTE]: (node, children) => <blockquote className="my-8 rounded-r-xl border-l-2 border-[#A2BC6B] bg-neutral-900/70 px-6 py-5 italic text-neutral-300">{children}</blockquote>,
        [BLOCKS.EMBEDDED_ASSET]: (node) => {
            const fields = node.data.target?.fields || {};
            const url = fields.file?.url;
            if (!url) return null;
            return <figure className="my-10"><img src={url.startsWith('//') ? `https:${url}` : url} alt={fields.title || 'Project visual'} className="w-full rounded-2xl border border-neutral-800" />{fields.description && <figcaption className="mt-3 text-sm text-neutral-500">{fields.description}</figcaption>}</figure>;
        },
        [INLINES.HYPERLINK]: (node, children) => <a href={node.data.uri} target="_blank" rel="noopener noreferrer" className="text-[#A2BC6B] underline decoration-[#A2BC6B]/30 underline-offset-4 hover:decoration-[#A2BC6B]">{children}</a>,
    },
};

const hasContent = (content) => {
    if (!content) return false;
    if (typeof content === 'string') return content.trim().length > 0;
    if (Array.isArray(content)) return content.length > 0;
    return Array.isArray(content.content) && content.content.length > 0;
};

const Content = ({ value }) => {
    if (typeof value === 'string') return <p className="text-base leading-8 text-neutral-400 md:text-lg">{value}</p>;
    if (Array.isArray(value)) return <ul className="ml-5 list-disc space-y-3 text-neutral-400 marker:text-[#A2BC6B]">{value.map((item, index) => <li key={index}>{item}</li>)}</ul>;
    return documentToReactComponents(value, richTextOptions);
};

const Arrow = () => <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M7 17 17 7M7 7h10v10" /></svg>;

const ProjectSection = ({ id, number, title, content }) => {
    if (!hasContent(content)) return null;
    return (
        <section id={id} className="scroll-mt-28 border-t border-neutral-800 py-14 md:py-20">
            <div className="grid gap-7 md:grid-cols-[150px_1fr] md:gap-12">
                <div>
                    <span className="text-xs font-semibold tracking-[0.24em] text-[#A2BC6B]">{number}</span>
                    <h2 className="mt-3 text-sm font-semibold uppercase tracking-[0.14em] text-neutral-300">{title}</h2>
                </div>
                <div className="min-w-0"><Content value={content} /></div>
            </div>
        </section>
    );
};

const MoodboardSection = ({ number, images }) => (
    <section id="moodboard" className="scroll-mt-28 border-t border-neutral-800 py-14 md:py-20">
        <div className="mb-8 grid gap-7 md:grid-cols-[150px_1fr] md:gap-12">
            <div>
                <span className="text-xs font-semibold tracking-[0.24em] text-[#A2BC6B]">{number}</span>
                <h2 className="mt-3 text-sm font-semibold uppercase tracking-[0.14em] text-neutral-300">Moodboard</h2>
            </div>
        </div>
        {images.length > 0 && (
            <div className="grid auto-rows-[180px] grid-cols-2 gap-3 sm:auto-rows-[220px] md:grid-cols-3">
                {images.map((image, index) => (
                    <figure key={`${image.url}-${index}`} className={`overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900 ${index === 0 ? 'col-span-2 row-span-2' : ''}`}>
                        <img src={image.url} alt={image.title || `Moodboard reference ${index + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.03]" />
                    </figure>
                ))}
            </div>
        )}
    </section>
);

const ProjectPage = () => {
    const { slug } = useParams();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        window.scrollTo(0, 0);
        setLoading(true);
        setError(null);
        fetchProjectBySlug(slug)
            .then((data) => {
                if (!mounted) return;
                if (data) setProject(data);
                else setError('Project not found');
            })
            .catch(() => mounted && setError('Failed to load project'))
            .finally(() => mounted && setLoading(false));
        return () => { mounted = false; };
    }, [slug]);

    const sections = useMemo(() => project ? [
        ['overview', 'Overview', project.overview || project.details],
        ['problem', 'The challenge', project.theProblem],
        ['goals', 'Goals & success', project.goals],
        ['responsibilities', 'My responsibilities', project.responsibilities],
        ['research', 'Discovery & research', project.discoveryAndResearch],
        ['moodboard', 'Moodboard', project.moodboardDescription, project.moodboard?.length > 0],
        ['constraints', 'Constraints', project.constraints],
        ['process', 'Process & approach', project.process],
        ['solution', 'The solution', project.solutionOverview],
        ['decisions', 'Key decisions & build', project.productDecisionsAndImplementation],
        ['challenges', 'Challenges', project.challenges],
        ['impact', 'Impact & outcomes', project.impact || project.results],
        ['learnings', 'Reflections & learnings', project.learnings],
        ['next-steps', 'Next steps', project.nextSteps],
    ].filter((section) => hasContent(section[2]) || section[3]) : [], [project]);

    if (loading) return (
        <div className="page flex min-h-screen flex-col justify-between pt-20">
            <Navbar />
            <div className="flex flex-1 items-center justify-center">
                <div className="flex items-center justify-center">
                    <svg className="h-8 w-8 animate-spin text-neutral-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    <span className="ml-3 text-neutral-500">Loading project...</span>
                </div>
            </div>
            <Footer />
        </div>
    );

    if (error || !project) return <div className="page flex min-h-screen flex-col pt-20"><Navbar /><div className="flex flex-1 flex-col items-center justify-center px-6 text-center"><p className="mb-3 text-xs font-semibold uppercase tracking-[0.24em] text-[#A2BC6B]">404 / Case study</p><h1 className="mb-4 text-4xl font-semibold text-white">Project not found</h1><p className="mb-8 max-w-md text-neutral-500">{error || 'This case study is no longer available.'}</p><Link to="/" className="rounded-full bg-[#A2BC6B] px-6 py-3 text-sm font-semibold text-black">Back to all work</Link></div><Footer /></div>;

    const categories = Array.isArray(project.category) ? project.category : [project.category || project.projectType].filter(Boolean);
    const tools = Array.isArray(project.tools) ? project.tools.join(', ') : project.tools;

    return (
        <div className="page min-h-screen bg-[#070708] pt-20">
            <Navbar />

            <header className="mx-auto max-w-7xl px-6 pb-14 pt-16 md:px-10 md:pb-20 md:pt-24">
                <Link to="/" className="mb-12 inline-flex items-center gap-2 text-sm text-neutral-500 transition-colors hover:text-white">← All projects</Link>
                <div className="grid items-end gap-10 lg:grid-cols-[1fr_280px]">
                    <div>
                        <div className="mb-6 flex flex-wrap gap-2">{categories.map((category) => <span key={category} className="rounded-full border border-[#A2BC6B]/25 bg-[#A2BC6B]/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#A2BC6B]">{String(category).replaceAll('_', ' ')}</span>)}</div>
                        <h1 className="max-w-5xl text-4xl font-semibold leading-[1.05] tracking-[-0.04em] text-white sm:text-6xl lg:text-7xl">{project.title}</h1>
                        {project.details && <p className="mt-7 max-w-3xl text-lg leading-8 text-neutral-400 md:text-xl">{project.details}</p>}
                    </div>
                    {(project.link && project.link !== '#') && <a href={project.link} target="_blank" rel="noopener noreferrer" className="inline-flex w-fit items-center gap-3 rounded-full bg-[#A2BC6B] px-6 py-3.5 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5">View live project <Arrow /></a>}
                </div>

                <dl className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8 border-t border-neutral-800 pt-8 md:grid-cols-4">
                    {[['Role', project.myRole || 'Designer & Developer'], ['Timeline', project.timeline || project.duration || 'Ongoing'], ['Team', project.team || 'Independent'], ['Tools', tools || '—']].map(([label, value]) => <div key={label}><dt className="mb-2 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-600">{label}</dt><dd className="text-sm leading-6 text-neutral-300">{value}</dd></div>)}
                </dl>
            </header>

            {project.image && <div className="mx-auto max-w-[1440px] px-3 md:px-6"><div className="overflow-hidden rounded-2xl border border-neutral-800 bg-neutral-900"><img src={project.image} alt={`${project.title} project cover`} className="aspect-[16/9] w-full object-cover" /></div></div>}

            <main className="mx-auto grid max-w-6xl gap-12 px-6 py-14 md:px-10 md:py-24 lg:grid-cols-[220px_1fr] lg:gap-20">
                <aside className="hidden lg:block"><nav aria-label="Case study sections" className="sticky top-28"><p className="mb-5 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-600">In this case study</p><ol className="space-y-3">{sections.map(([id, title], index) => <li key={id}><a href={`#${id}`} className="group flex gap-3 text-sm text-neutral-500 transition-colors hover:text-white"><span className="text-neutral-700 group-hover:text-[#A2BC6B]">{String(index + 1).padStart(2, '0')}</span>{title}</a></li>)}</ol></nav></aside>
                <div>{sections.map(([id, title, content], index) => id === 'moodboard'
                    ? <MoodboardSection key={id} number={String(index + 1).padStart(2, '0')} images={project.moodboard || []} />
                    : <ProjectSection key={id} id={id} number={String(index + 1).padStart(2, '0')} title={title} content={content} />
                )}</div>
            </main>

            {project.gallery?.length > 0 && <section className="mx-auto max-w-7xl px-6 pb-24 md:px-10"><div className="mb-8 flex items-end justify-between border-t border-neutral-800 pt-14"><div><p className="mb-3 text-xs font-semibold tracking-[0.24em] text-[#A2BC6B]">VISUALS</p><h2 className="text-3xl font-semibold text-white md:text-4xl">A closer look</h2></div><span className="text-sm text-neutral-600">{project.gallery.length} images</span></div><div className="grid gap-5 md:grid-cols-2">{project.gallery.map((asset, index) => <figure key={`${asset.url}-${index}`} className={index % 3 === 0 ? 'md:col-span-2' : ''}><img src={asset.url} alt={asset.title || `${project.title} visual ${index + 1}`} loading="lazy" className="w-full rounded-2xl border border-neutral-800 bg-neutral-900 object-cover" />{asset.title && <figcaption className="mt-3 text-sm leading-6 text-neutral-400">{asset.title}</figcaption>}</figure>)}</div></section>}

            <section className="border-y border-neutral-800 bg-neutral-900/30"><div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-8 px-6 py-16 md:flex-row md:items-center md:px-10"><div><p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#A2BC6B]">End of case study</p><h2 className="text-3xl font-semibold text-white">Want to see more of my work?</h2></div><div className="flex flex-wrap gap-3">{project.secondaryLink && <a href={project.secondaryLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-neutral-700 px-5 py-3 text-sm text-neutral-200 hover:border-neutral-500">{project.secondaryLinkLabel} <Arrow /></a>}<Link to="/" className="inline-flex items-center gap-2 rounded-full bg-[#A2BC6B] px-5 py-3 text-sm font-semibold text-black">Explore all projects <Arrow /></Link></div></div></section>

            <Footer />
        </div>
    );
};

export default ProjectPage;
